// client/src/components/AdminSupport.js
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import AdminNavbar from './AdminNavbar';
import '../CSS/Dashboard.css';
import { firestore } from '../../firebase';
import { AuthContext } from '../../AuthContext';

const THREAD_COLLECTION = 'supportMessages';

const sectionStyles = {
  list: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  card: {
    background: 'var(--eb-surface)',
    border: '1px solid var(--eb-border)',
    borderRadius: 'var(--eb-radius-lg)',
    boxShadow: 'var(--eb-shadow-sm)',
    padding: '20px 24px',
    minWidth: 0,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
    flexWrap: 'wrap',
    paddingBottom: '14px',
    borderBottom: '1px solid var(--eb-border)',
  },
  statusChip: (variant) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 10px',
    borderRadius: 'var(--eb-radius-pill)',
    fontSize: '12.5px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    background: variant === 'pending' ? 'var(--eb-warning-bg)' : 'var(--eb-success-bg)',
    color: variant === 'pending' ? 'var(--eb-warning)' : 'var(--eb-success)',
    border: `1px solid ${variant === 'pending' ? '#fde3b0' : '#c6f0da'}`,
  }),
  conversation: {
    marginTop: '14px',
  },
  scrollArea: {
    maxHeight: '340px',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '14px',
    background: 'var(--eb-surface-2)',
    border: '1px solid var(--eb-border)',
    borderRadius: 'var(--eb-radius)',
  },
  messageBubble: (sender) => ({
    alignSelf: sender === 'admin' ? 'flex-end' : 'flex-start',
    maxWidth: '80%',
    padding: '10px 12px',
    borderRadius: '14px',
    fontSize: '14px',
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    background: sender === 'admin' ? 'var(--eb-brand)' : 'var(--eb-surface)',
    color: sender === 'admin' ? '#fff' : 'var(--eb-text)',
    border: sender === 'admin' ? '1px solid var(--eb-brand)' : '1px solid var(--eb-border)',
    borderBottomRightRadius: sender === 'admin' ? '4px' : '14px',
    borderBottomLeftRadius: sender === 'admin' ? '14px' : '4px',
  }),
  bubbleMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '6px',
    fontSize: '11.5px',
    opacity: 0.75,
  },
  bubbleDelete: {
    marginLeft: 'auto',
    border: 'none',
    background: 'transparent',
    color: 'inherit',
    cursor: 'pointer',
    fontSize: '12px',
    padding: '2px 4px',
    borderRadius: '4px',
    opacity: 0.8,
  },
  textarea: {
    width: '100%',
    minHeight: '88px',
    resize: 'vertical',
    padding: '10px 12px',
    fontSize: '14.5px',
    lineHeight: 1.45,
    borderRadius: 'var(--eb-radius-sm)',
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '12px',
  },
  sectionTitle: {
    margin: '0 0 12px',
    fontSize: '16px',
    fontWeight: 650,
    color: 'var(--eb-text)',
  },
  loadingText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    color: 'var(--eb-muted)',
    fontSize: '14px',
    margin: 0,
    padding: '32px 16px',
  },
  userInfo: {
    fontSize: '13px',
    color: 'var(--eb-muted)',
    overflowWrap: 'anywhere',
  },
  userName: {
    fontSize: '16px',
    fontWeight: 650,
    color: 'var(--eb-text)',
    marginBottom: '2px',
    overflowWrap: 'anywhere',
  },
  lastMessageTime: {
    fontSize: '12.5px',
    color: 'var(--eb-subtle)',
    marginTop: '4px',
  },
  noMessagesText: {
    textAlign: 'center',
    color: 'var(--eb-muted)',
    fontSize: '14px',
    margin: 0,
    padding: '24px 12px',
  },
};

const formatDate = (ts) => {
  if (!ts) return 'Date inconnue';
  try {
    const date = ts.toDate ? ts.toDate() : ts;
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Date inconnue';
  }
};

const AdminSupport = () => {
  const { currentUser } = useContext(AuthContext);
  const [threads, setThreads] = useState([]);
  const [messagesByThread, setMessagesByThread] = useState({});
  const [loading, setLoading] = useState(true);
  const [flash, setFlash] = useState('');
  const [draftReplies, setDraftReplies] = useState({});
  const [userCache, setUserCache] = useState({});
  const messageListenersRef = useRef({});
  const navigate = useNavigate();
  const scrollRefs = useRef({});
  const prevScrollHeightRef = useRef({});

  useEffect(() => {
    const q = query(collection(firestore, THREAD_COLLECTION), orderBy('lastMessageAt', 'desc'), limit(50));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const payload = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setThreads(payload);
        setLoading(false);
      },
      (err) => {
        console.error('AdminSupport snapshot error:', err);
        setFlash('Impossible de charger les conversations.');
        setLoading(false);
      },
    );

    return unsubscribe;
  }, []);

  const refreshThreadMeta = useCallback(async (threadId) => {
    try {
      const messagesRef = collection(firestore, THREAD_COLLECTION, threadId, 'messages');
      const lastSnap = await getDocs(query(messagesRef, orderBy('createdAt', 'desc'), limit(1)));
      if (lastSnap.empty) {
        await updateDoc(doc(firestore, THREAD_COLLECTION, threadId), {
          lastMessageAt: null,
          lastMessageSender: null,
          lastMessagePreview: '',
          hasUnansweredUserMessage: false,
        });
        return;
      }

      const last = lastSnap.docs[0]?.data() || {};
      await updateDoc(doc(firestore, THREAD_COLLECTION, threadId), {
        lastMessageAt: last.createdAt || null,
        lastMessageSender: last.sender || '',
        lastMessagePreview: last.content ? last.content.slice(0, 120) : '',
        hasUnansweredUserMessage: last.sender === 'user',
      });
    } catch (err) {
      console.error('AdminSupport refresh meta error:', err);
    }
  }, []);

  // Les anciennes conversations ne sont plus migrées automatiquement : la nouvelle
  // structure utilise déjà supportMessages/{uid}/messages côté client et admin.

  useEffect(() => {
    const listeners = messageListenersRef.current;
    const nextIds = new Set(threads.map((thread) => thread.id));

    Object.keys(listeners).forEach((id) => {
      if (!nextIds.has(id)) {
        listeners[id]?.();
        delete listeners[id];
        setMessagesByThread((prev) => {
          const copy = { ...prev };
          delete copy[id];
          return copy;
        });
      }
    });

    threads.forEach((thread) => {
      if (listeners[thread.id]) return;
      const subRef = collection(firestore, THREAD_COLLECTION, thread.id, 'messages');
      const q = query(subRef, orderBy('createdAt', 'asc'));
      listeners[thread.id] = onSnapshot(
        q,
        (snapshot) => {
          const arr = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
          setMessagesByThread((prev) => ({ ...prev, [thread.id]: arr }));
        },
        (err) => {
          console.error('AdminSupport messages snapshot error:', err);
        },
      );
    });
  }, [threads]);

  useEffect(() => () => {
    Object.values(messageListenersRef.current).forEach((unsub) => unsub?.());
    messageListenersRef.current = {};
  }, []);

  useEffect(() => {
    const needed = Array.from(
      new Set(
        threads
          .map((item) => item.userId)
          .filter(Boolean)
          .filter((uid) => !userCache[uid]),
      ),
    );
    if (!needed.length) return;

    let cancelled = false;
    (async () => {
      const updates = {};
      for (const uid of needed) {
        try {
          const snap = await getDoc(doc(firestore, 'users', uid));
          updates[uid] = snap.exists() ? snap.data() : null;
        } catch (err) {
          console.error('AdminSupport user lookup error:', err);
        }
      }
      if (!cancelled && Object.keys(updates).length) {
        setUserCache((prev) => ({ ...prev, ...updates }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [threads, userCache]);

  useEffect(() => {
    Object.keys(messagesByThread).forEach((threadId) => {
      const scrollArea = scrollRefs.current[threadId];
      if (scrollArea) {
        const prevScrollHeight = prevScrollHeightRef.current[threadId] || 0;
        const isScrolledToBottom =
          scrollArea.scrollTop + scrollArea.clientHeight >= prevScrollHeight - 30;

        if (isScrolledToBottom) {
          scrollArea.scrollTop = scrollArea.scrollHeight;
        }

        prevScrollHeightRef.current[threadId] = scrollArea.scrollHeight;
      }
    });
  }, [messagesByThread]);

  const handleReplyChange = (threadId, value) => {
    setDraftReplies((prev) => ({ ...prev, [threadId]: value }));
  };

  const sendReply = async (thread) => {
    const text = (draftReplies[thread.id] || '').trim();
    if (!text || !currentUser) return;

    try {
      const threadRef = doc(firestore, THREAD_COLLECTION, thread.id);
      await addDoc(collection(threadRef, 'messages'), {
        sender: 'admin',
        content: text,
        createdAt: serverTimestamp(),
      });

      await updateDoc(threadRef, {
        lastMessageAt: serverTimestamp(),
        lastMessageSender: 'admin',
        lastMessagePreview: text.slice(0, 120),
        hasUnansweredUserMessage: false,
        adminResponderId: currentUser.uid,
        adminResponderEmail: currentUser.email || '',
      });

      setDraftReplies((prev) => ({ ...prev, [thread.id]: '' }));
      setFlash('Reponse envoyee.');
    } catch (err) {
      console.error('AdminSupport reply error:', err);
      setFlash("Impossible d'envoyer la reponse.");
    }
  };

  const markAsRead = async (thread) => {
    if (!currentUser) return;

    try {
      const threadRef = doc(firestore, THREAD_COLLECTION, thread.id);
      await updateDoc(threadRef, {
        hasUnansweredUserMessage: false,
        adminResponderId: currentUser.uid,
        adminResponderEmail: currentUser.email || '',
      });
      setFlash('Conversation marquee comme lue.');
    } catch (err) {
      console.error('AdminSupport mark as read error:', err);
      setFlash('Impossible de marquer comme lu.');
    }
  };

  const handleDeleteMessage = async (threadId, messageId) => {
    const confirmed = window.confirm('Supprimer ce message ?');
    if (!confirmed) return;
    try {
      await deleteDoc(doc(firestore, THREAD_COLLECTION, threadId, 'messages', messageId));
      await refreshThreadMeta(threadId);
      setFlash('Message supprime.');
    } catch (err) {
      console.error('AdminSupport delete message error:', err);
      setFlash('Suppression impossible.');
    }
  };

  const openUserDetails = async (thread) => {
    if (!thread.userId) return;
    let payload = userCache[thread.userId];
    if (!payload) {
      try {
        const snap = await getDoc(doc(firestore, 'users', thread.userId));
        payload = snap.exists() ? snap.data() : null;
        setUserCache((prev) => ({ ...prev, [thread.userId]: payload }));
      } catch (err) {
        console.error('AdminSupport fallback user fetch error:', err);
      }
    }

    const userPayload = payload
      ? { id: thread.userId, ...payload }
      : { id: thread.userId, email: thread.userEmail || '', role: thread.userRole || 'client' };

    navigate('/adminusersviewer', { state: { user: userPayload } });
  };

  const renderConversation = (thread) => {
    const conversation = messagesByThread[thread.id] || [];
    if (!conversation.length) {
      return <p style={sectionStyles.noMessagesText}>Aucun message pour l'instant.</p>;
    }
    return (
      <div style={sectionStyles.scrollArea} ref={(el) => (scrollRefs.current[thread.id] = el)}>
        {conversation.map((msg) => (
          <div key={msg.id} style={sectionStyles.messageBubble(msg.sender)}>
            <div>{msg.content}</div>
            <div style={sectionStyles.bubbleMeta}>
              <span>{msg.sender === 'admin' ? 'Equipe' : 'Utilisateur'}</span>
              <span>{formatDate(msg.createdAt)}</span>
              <button
                type="button"
                style={sectionStyles.bubbleDelete}
                aria-label="Supprimer le message"
                onClick={() => handleDeleteMessage(thread.id, msg.id)}
              >
                &#x2715;
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderThreadCard = (thread) => {
    const userData = userCache[thread.userId] || {};
    const displayName =
      userData.pseudo ||
      userData.companyName ||
      thread.displayName ||
      thread.userEmail ||
      thread.userId ||
      'Utilisateur';
    const replyValue = draftReplies[thread.id] ?? '';

    return (
      <div key={thread.id} style={sectionStyles.card}>
        <div style={sectionStyles.header}>
          <div>
            <div style={sectionStyles.userName}>{displayName}</div>
            <div style={sectionStyles.userInfo}>
              UID: {thread.userId || 'Inconnu'} - Role: {thread.userRole || 'client'}
            </div>
            {thread.userEmail && (
              <div style={sectionStyles.userInfo}>{thread.userEmail}</div>
            )}
            <div style={sectionStyles.lastMessageTime}>
              Dernier message: {formatDate(thread.lastMessageAt)}
            </div>
          </div>
          <span style={sectionStyles.statusChip(thread.hasUnansweredUserMessage ? 'pending' : 'ok')}>
            {thread.hasUnansweredUserMessage ? 'Nouveaux messages' : 'A jour'}
          </span>
        </div>

        <div style={sectionStyles.conversation}>{renderConversation(thread)}</div>

        <div style={{ marginTop: '12px' }}>
          <textarea
            className="eb-input"
            style={sectionStyles.textarea}
            placeholder="Repondre a cet utilisateur..."
            value={replyValue}
            onChange={(e) => handleReplyChange(thread.id, e.target.value)}
          />
        </div>

        <div style={sectionStyles.actions}>
          <button
            type="button"
            className="eb-btn eb-btn--secondary eb-btn--sm"
            onClick={() => openUserDetails(thread)}
          >
            Fiche utilisateur
          </button>
          {thread.hasUnansweredUserMessage && (
            <button
              type="button"
              className="eb-btn eb-btn--secondary eb-btn--sm"
              onClick={() => markAsRead(thread)}
            >
              Marquer comme lu
            </button>
          )}
          <button
            type="button"
            className="eb-btn eb-btn--primary eb-btn--sm"
            disabled={!replyValue.trim()}
            onClick={() => sendReply(thread)}
          >
            Envoyer la reponse
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-container">
      <AdminNavbar />
      <main style={{ width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <header className="eb-page-header" style={{ marginBottom: 8 }}>
          <div>
            <h1 className="eb-page-title" style={{ margin: 0, textAlign: 'left' }}>Support client</h1>
            <p className="eb-page-subtitle">Conversations avec les utilisateurs</p>
          </div>
          {!loading && <span className="eb-badge">{threads.length} conversation{threads.length > 1 ? 's' : ''}</span>}
        </header>
        {flash && <div className="eb-alert eb-alert--info">{flash}</div>}
        {loading ? (
          <div className="eb-card">
            <p style={sectionStyles.loadingText}><span className="eb-spinner eb-spinner--sm" /> Chargement des conversations...</p>
          </div>
        ) : (
          <div style={sectionStyles.list}>
            {threads.length === 0 ? (
              <div className="eb-card"><p style={sectionStyles.noMessagesText}>Aucune conversation.</p></div>
            ) : (
              threads.map((thread) => renderThreadCard(thread))
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminSupport;
